import { EditOutlined } from "@ant-design/icons";
import { Flex, Input, message, Switch, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, BackgroundVariant, ReactFlowProvider, type Edge, type Node, type NodeChange } from "reactflow";
import "reactflow/dist/style.css";
import HButton from "../../components/custom/input/button/Button";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import { CommonLoadingV2, NoChannelPresent } from "../utils/CommonVessels";
import { ActionConfigPanel } from "./components/ActionConfigPanel";
import { ConditionConfigPanel } from "./components/ConditionConfigPanel";
import { WorkflowCustomControls } from "./components/WorkflowCustomControls";
import { ConnectorEdge } from "./edges/ConnectorEdge";
import { useWorkflowEnums } from "./hooks/useWorkflowEnums";
import { ActionNode } from "./nodes/ActionNode";
import { AddStepNode } from "./nodes/AddStepNode";
import { ConditionNode } from "./nodes/ConditionNode";
import { EndNode } from "./nodes/EndNode";
import { Trigger, type TriggerType } from "./nodes/TriggerNode";
import { TriggerNode } from "./nodes/TriggerNode";
import type { ServerWorkflow } from "./parser/types";
import type { StepDefinition } from "./parser/workflowParser";
import type { WorkflowEnumItem } from "./hooks/useWorkflowEnums";
import { useWorkflowStore } from "./store";
import { selectHasChanged, selectSelectedStep } from "./store/selectors";
import "./triggerRules.css";
import type { WorkflowRecord } from "./TriggerRulesList";

const { Text } = Typography;

/** Maps store triggerType to API trigger code used in supportedTriggers (e.g. Trigger.Message.Inbound) */
const TRIGGER_TYPE_TO_CODE: Record<TriggerType, string> = {
    [Trigger.Message.Inbound]: Trigger.Message.Inbound,
    [Trigger.Message.Outbound]: Trigger.Message.Outbound,
};

function filterBySupportedTriggers<T extends WorkflowEnumItem>(
    items: T[],
    triggerLabel: string
): T[] {
    return items.filter((item) => {
        const supported = item.meta?.supportedTriggers;
        if (!supported || supported.length === 0) return true;
        return supported.includes(triggerLabel);
    });
}

const NODE_TYPES = {
    trigger: TriggerNode,
    condition: ConditionNode,
    action: ActionNode,
    addStep: AddStepNode,
    end: EndNode,
};

const EDGE_TYPES = { default: ConnectorEdge };

const NODE_WIDTH = 350;
const ADD_STEP_SIZE = 32;
const ADD_STEP_GAP = 80;
const DEFAULT_NODE_HEIGHT = 100;

function isParentStepConfigured(step: StepDefinition): boolean {
    if (step.type === "trigger") return true;

    if (step.type === "condition") {
        const data = (step.data ?? {}) as { isPlaceholder?: boolean; conditionCode?: string };
        return !data.isPlaceholder && Boolean(data.conditionCode);
    }

    if (step.type === "action") {
        const data = (step.data ?? {}) as { isPlaceholder?: boolean; actionCode?: string; actionType?: string };
        return !data.isPlaceholder && Boolean(data.actionCode || data.actionType);
    }

    return true;
}

function buildFlowFromSteps(
    steps: StepDefinition[],
    selectedNodeId: string | null,
    measuredHeights: Record<string, number>,
    addStepCloseSignal: number
): { nodes: Node[]; edges: Edge[] } {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeX = 0;
    const addStepX = (NODE_WIDTH - ADD_STEP_SIZE) / 2;

    let y = 0;

    for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        const nodeHeight = measuredHeights[step.id] ?? DEFAULT_NODE_HEIGHT;

        nodes.push({
            id: step.id,
            type: step.type,
            position: { x: nodeX, y },
            data: step.data,
            draggable: false,
            selected: selectedNodeId === step.id,
        });

        const addStepId = `addstep-after-${step.id}`;
        const addStepY = y + nodeHeight + (ADD_STEP_GAP - ADD_STEP_SIZE) / 2;

        nodes.push({
            id: addStepId,
            type: "addStep",
            position: { x: addStepX, y: addStepY },
            data: {
                insertIndex: i + 1,
                parentStepId: step.id,
                isEnabled: isParentStepConfigured(step),
                closeSignal: addStepCloseSignal,
            },
            draggable: false,
        });

        edges.push({
            id: `e-${step.id}-${addStepId}`,
            source: step.id,
            target: addStepId,
        });

        if (i + 1 < steps.length) {
            edges.push({
                id: `e-${addStepId}-${steps[i + 1].id}`,
                source: addStepId,
                target: steps[i + 1].id,
            });
        }

        y += nodeHeight + ADD_STEP_GAP;
    }

    if (steps.length > 0) {
        const lastStep = steps[steps.length - 1];
        const lastStepHeight = measuredHeights[lastStep.id] ?? DEFAULT_NODE_HEIGHT;
        const lastStepY = y - (lastStepHeight + ADD_STEP_GAP);
        const lastAddStepId = `addstep-after-${lastStep.id}`;
        const lastAddStepY = lastStepY + lastStepHeight + (ADD_STEP_GAP - ADD_STEP_SIZE) / 2;
        const endNodeId = "workflow-end-node";

        nodes.push({
            id: endNodeId,
            type: "end",
            position: { x: addStepX - 6, y: lastAddStepY + ADD_STEP_SIZE + 10 },
            data: {},
            draggable: false,
            selectable: false,
        });
        edges.push({
            id: `e-${lastAddStepId}-${endNodeId}`,
            source: lastAddStepId,
            target: endNodeId,
        });
    }

    return { nodes, edges };
}

export interface TriggerRulesEditorProps {
    workflow: WorkflowRecord | null;
    serverWorkflow?: ServerWorkflow | null;
    onBack: () => void;
    triggerType?: TriggerType;
}

const TriggerRulesEditorContent: React.FC<TriggerRulesEditorProps> = ({
    workflow,
    serverWorkflow,
    onBack,
    triggerType = Trigger.Message.Inbound,
}) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const { conditionTypes, actionTypes, scheduleOptions } = useWorkflowEnums();

    const storeTriggerType = useWorkflowStore((s) => s.triggerType);
    const triggerCode = TRIGGER_TYPE_TO_CODE[storeTriggerType];
    const filteredConditionTypes = useMemo(
        () => filterBySupportedTriggers(conditionTypes, triggerCode),
        [conditionTypes, triggerCode]
    );
    const filteredActionTypes = useMemo(
        () => filterBySupportedTriggers(actionTypes, triggerCode),
        [actionTypes, triggerCode]
    );

    const steps = useWorkflowStore((s) => s.steps);
    const selectedNodeId = useWorkflowStore((s) => s.selectedNodeId);
    const workflowName = useWorkflowStore((s) => s.workflowName);
    const measuredHeights = useWorkflowStore((s) => s.measuredHeights);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const setWorkflowName = useWorkflowStore((s) => s.setWorkflowName);
    const updateMeasuredHeights = useWorkflowStore((s) => s.updateMeasuredHeights);
    const deleteStep = useWorkflowStore((s) => s.deleteStep);
    const addStep = useWorkflowStore((s) => s.addStep);

    const selectedStep = useWorkflowStore(selectSelectedStep);
    const hasChanged = useWorkflowStore(selectHasChanged);
    const serverStatus = useWorkflowStore((s) => s.serverStatus);
    const workflowId = useWorkflowStore((s) => s.workflowId);
    const [saving, setSaving] = useState(false);
    const [toggleStatusLoading, setToggleStatusLoading] = useState(false);
    const [addStepCloseSignal, setAddStepCloseSignal] = useState(0);

    useEffect(() => {
        if (serverWorkflow?.graph) {
            useWorkflowStore.getState().loadFromServer(serverWorkflow);
        } else {
            useWorkflowStore.getState().initNewWorkflow(triggerType);
        }
    }, [serverWorkflow, triggerType]);

    useEffect(() => {
        if (workflow?.workflowName) {
            setWorkflowName(workflow.workflowName);
        }
    }, [workflow?.workflowName, setWorkflowName]);

    useEffect(() => {
        return () => {
            useWorkflowStore.getState().clearStore();
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const currentSelectedId = useWorkflowStore.getState().selectedNodeId;
            if (!currentSelectedId) return;

            const target = e.target as HTMLElement;
            if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;

            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                deleteStep(currentSelectedId);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [deleteStep]);

    const { nodes, edges } = useMemo(
        () => buildFlowFromSteps(steps, selectedNodeId, measuredHeights, addStepCloseSignal),
        [steps, selectedNodeId, measuredHeights, addStepCloseSignal],
    );

    const handleNodesChange = useCallback(
        (changes: NodeChange[]) => {
            const heightChanges: Array<{ id: string; height: number }> = [];
            for (const change of changes) {
                if (change.type === "dimensions" && change.dimensions?.height) {
                    heightChanges.push({ id: change.id, height: change.dimensions.height });
                }
            }
            if (heightChanges.length > 0) {
                requestAnimationFrame(() => updateMeasuredHeights(heightChanges));
            }
        },
        [updateMeasuredHeights],
    );

    const onNodeClick = useCallback(
        (_: React.MouseEvent, node: Node) => {
            if (node.type === "condition" || node.type === "action") {
                selectNode(node.id);
            }
        },
        [selectNode],
    );

    const onPaneClick = useCallback(
        (e: React.MouseEvent) => {
            // Don't deselect when clicking Add step popover overlay (portal-rendered)
            const target = e.target as HTMLElement;
            if (target?.closest?.(".add-step-popover-overlay") || target?.closest?.(".add-step-popover")) {
                return;
            }
            setAddStepCloseSignal((prev) => prev + 1);
            selectNode(null);
        },
        [selectNode]
    );

    const onMoveStart = useCallback(() => {
        setAddStepCloseSignal((prev) => prev + 1);
    }, []);

    const handleCancel = useCallback(() => {
        onBack();
    }, [onBack]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const result = await useWorkflowStore.getState().saveWorkflow({ fetchData, appProperties });

            if (!result.success && result.validationErrors.length > 0) {
                const firstError = result.validationErrors[0];
                message.warning(firstError.message);
                if (firstError.stepId) {
                    selectNode(firstError.stepId);
                }
                return;
            }

            message.success("Workflow saved successfully.");
        } catch {
            message.error("Failed to save workflow.");
        } finally {
            setSaving(false);
        }
    }, [fetchData, appProperties, selectNode]);

    const isWorkflowEnabled = (serverStatus ?? "").toLowerCase() === "active";

    const handleToggleWorkflowStatus = useCallback(
        async (enabled: boolean) => {
            if (!workflowId || !appProperties) return;

            // Simple rule:
            // - Switch ON  -> request ACTIVE
            // - Switch OFF -> request PAUSED
            // Server decides how to handle DRAFT/PAUSED/ACTIVE transitions.
            setToggleStatusLoading(true);
            try {
                const result = await useWorkflowStore
                    .getState()
                    .setWorkflowEnabled({ fetchData, appProperties }, enabled);
                if (result.success) {
                    message.success(
                        enabled ? "Workflow enabled successfully." : "Workflow disabled successfully."
                    );
                } else {
                    message.error(
                        enabled ? "Failed to enable workflow." : "Failed to disable workflow."
                    );
                }
            } catch {
                message.error(
                    enabled ? "Failed to enable workflow." : "Failed to disable workflow."
                );
            } finally {
                setToggleStatusLoading(false);
            }
        },
        [workflowId, appProperties, fetchData]
    );

    const handleQuickAddStep = useCallback(
        (type: "condition" | "action") => {
            addStep(steps.length, type);
        },
        [addStep, steps.length]
    );

    if (!appProperties) return <CommonLoadingV2 />;
    if (!appProperties.serviceInstalled) return <NoChannelPresent newTab={false} />;

    return (
        <Flex className="trigger-rules">
            <div className="trigger-rules__main">
                <Flex className="py-3 px-3 hs-border-bottom-light" justify="space-between" align="center">
                    <Flex align="center" gap={12}>
                        <Input
                            value={workflowName}
                            onChange={(e) => setWorkflowName(e.target.value)}
                            className="trigger-rules__rule-name-input"
                            suffix={<EditOutlined className="trigger-rules__edit-icon" aria-hidden />}
                            aria-label="Workflow name"
                        />
                    </Flex>
                    <Flex gap={12} wrap="wrap" align="center" className="trigger-rules__header-actions">
                            <Flex align="center" gap={8}>
                                <Text className="hs-fs-12 hs-color-mediumDark">Active</Text>
                                <Switch
                                    className="trigger-rules__status-switch"
                                    checked={isWorkflowEnabled}
                                    onChange={handleToggleWorkflowStatus}
                                    disabled={toggleStatusLoading || !workflowId}
                                    loading={toggleStatusLoading}
                                    aria-label={isWorkflowEnabled ? "Disable workflow" : "Enable workflow"}
                                />
                            </Flex>
                        <div className="trigger-rules__header-divider" aria-hidden />
                        <HButton type="text" onClick={handleCancel}>
                            Cancel
                        </HButton>
                        <HButton type="primary" onClick={handleSave} disabled={saving || !hasChanged}>
                            {saving ? (
                                <div className="d-inline-flex align-items-center justify-content-center w-100">
                                    <div className="actionIconsSprite dropDownLoading position-absolute" />
                                </div>
                            ) : (
                                "Save"
                            )}
                        </HButton>
                    </Flex>
                </Flex>

                <div className="trigger-rules__body" role="main" aria-label="Workflow canvas">
                    <div className="trigger-rules__canvas">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={handleNodesChange}
                            onNodeClick={onNodeClick}
                            onPaneClick={onPaneClick}
                            onMoveStart={onMoveStart}
                            nodeTypes={NODE_TYPES}
                            edgeTypes={EDGE_TYPES}
                            onlyRenderVisibleElements
                            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                            fitView
                            fitViewOptions={{ padding: 0.2, minZoom: 0.9, maxZoom: 1.5 }}
                            minZoom={0.3}
                            maxZoom={1.5}
                            panOnScroll
                            zoomOnScroll={false}
                            zoomOnPinch
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable
                            proOptions={{ hideAttribution: true }}
                        >
                            <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color="#d0d0d0" />
                            <WorkflowCustomControls />
                        </ReactFlow>
                    </div>
                </div>
            </div>
            <div className="trigger-rules__config-panel">
                {selectedStep?.type === "condition" ? (
                    <ConditionConfigPanel
                        conditionTypes={filteredConditionTypes}
                        savedNumbers={appProperties?.savedNumbers ?? []}
                        scheduleOptions={scheduleOptions}
                    />
                ) : selectedStep?.type === "action" ? (
                    <ActionConfigPanel actionTypes={filteredActionTypes} />
                ) : (
                    <div className="trigger-rule-config-panel">
                        <div className="trigger-rule-selector-panel__header">
                            <Text className="trigger-rule-config-panel__title hs-fw-600 hs-fs-16 hs-color-BlackRussian">
                                Trigger
                            </Text>
                        </div>
                        <div className="trigger-rule-config-panel__body trigger-rule-config-panel__body--empty">
                            <div className="trigger-rule-empty-state">
                                <div className="automationCardsSprite trigger-rule-empty-state__icon" />
                                <Text className="hs-fs-18 hs-color-BlackRussian hs-fw-600">Trigger is ready</Text>
                                <Text className="hs-fs-12 hs-color-mediumDark">Now add your first action step.</Text>
                                <button
                                    type="button"
                                    className="trigger-rule-empty-state__link"
                                    onClick={() => handleQuickAddStep("condition")}
                                >
                                    Add Condition
                                </button>
                                <button
                                    type="button"
                                    className="trigger-rule-empty-state__link"
                                    onClick={() => handleQuickAddStep("action")}
                                >
                                    Add Action
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Flex>
    );
};

const TriggerRulesEditor: React.FC<TriggerRulesEditorProps> = (props) => (
    <ReactFlowProvider>
        <TriggerRulesEditorContent {...props} />
    </ReactFlowProvider>
);

export default TriggerRulesEditor;
